import React, { useState } from 'react';

interface GovernanceStructureDisplayProps {
    data: any;         // Full daily_country_analysis / GeopoliticalAnalysisResult document
    country?: string;
    onNavigate?: (nav: { page: any; params?: any }) => void;
    onBack?: () => void;
}


// --- Colour palette per branch ---
const BRANCH_COLOURS: Record<string, { bg: string; border: string; text: string; badge: string }> = {
    executive:   { bg: 'bg-blue-900',   border: 'border-blue-500',   text: 'text-blue-100',   badge: 'bg-blue-500'   },
    legislative: { bg: 'bg-indigo-900', border: 'border-indigo-500', text: 'text-indigo-100', badge: 'bg-indigo-500' },
    judicial:    { bg: 'bg-purple-900', border: 'border-purple-500', text: 'text-purple-100', badge: 'bg-purple-500' },
    military:    { bg: 'bg-red-900',    border: 'border-red-500',    text: 'text-red-100',    badge: 'bg-red-500'    },
    intelligence:{ bg: 'bg-slate-800',  border: 'border-slate-500',  text: 'text-slate-100',  badge: 'bg-slate-500'  },
};

// WGI score label helper
function wgiLabel(score: number | null | undefined) {
    if (score == null) return { label: 'N/A', colour: 'text-slate-400' };
    const pct = Math.round((score + 2.5) / 5 * 100);
    if (pct >= 75) return { label: `Strong (${pct}th pct.)`, colour: 'text-emerald-400' };
    if (pct >= 50) return { label: `Moderate (${pct}th pct.)`, colour: 'text-yellow-400' };
    if (pct >= 25) return { label: `Weak (${pct}th pct.)`, colour: 'text-orange-400' };
    return { label: `Fragile (${pct}th pct.)`, colour: 'text-red-400' };
}

// Render a single score bar
const WgiBar: React.FC<{ label: string; value: number | null | undefined; icon: string }> = ({ label, value, icon }) => {
    const pct = value != null ? Math.round((value + 2.5) / 5 * 100) : null;
    const { label: lbl, colour } = wgiLabel(value);
    return (
        <div className="space-y-1">
            <div className="flex justify-between items-center">
                <span className="text-[11px] font-bold text-slate-300 flex items-center gap-1.5">{icon} {label}</span>
                <span className={`text-[11px] font-black ${colour}`}>{lbl}</span>
            </div>
            <div className="w-full bg-slate-700 h-1.5 rounded-full overflow-hidden">
                {pct != null && (
                    <div
                        className={`h-full rounded-full transition-all duration-700 ${pct >= 75 ? 'bg-emerald-500' : pct >= 50 ? 'bg-yellow-400' : pct >= 25 ? 'bg-orange-500' : 'bg-red-500'}`}
                        style={{ width: `${pct}%` }}
                    />
                )}
            </div>
        </div>
    );
};

// Vertical connector line between org-chart nodes
const Connector: React.FC = () => (
    <div className="flex justify-center my-1">
        <div className="w-0.5 h-6 bg-slate-600" />
    </div>
);

// Horizontal splitter bar for siblings
const HorizontalBar: React.FC<{ count: number }> = ({ count }) => {
    if (count <= 1) return null;
    return (
        <div className="flex justify-center my-1">
            <div className="w-0.5 h-4 bg-slate-600" />
            <div className="absolute h-0.5 bg-slate-600" style={{ width: `${count * 200}px` }} />
        </div>
    );
};

// A single org-chart node card
const OrgNode: React.FC<{
    title: string;
    subtitle?: string;
    description?: string;
    branch: keyof typeof BRANCH_COLOURS;
    icon?: string;
    badge?: string;
}> = ({ title, subtitle, description, branch, icon = '🏛️', badge }) => {
    const [expanded, setExpanded] = useState(false);
    const c = BRANCH_COLOURS[branch];
    return (
        <div
            className={`relative rounded-xl border-2 ${c.border} ${c.bg} shadow-xl cursor-pointer hover:scale-[1.02] transition-transform duration-200 min-w-[180px] max-w-[240px]`}
            onClick={() => setExpanded(!expanded)}
        >
            <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                    <div>
                        <div className="text-lg">{icon}</div>
                        <div className={`text-[11px] font-black uppercase tracking-wider mt-1 ${c.text}`}>{title}</div>
                        {subtitle && <div className="text-[10px] text-slate-400 mt-0.5">{subtitle}</div>}
                    </div>
                    {badge && (
                        <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full ${c.badge} text-white whitespace-nowrap`}>
                            {badge}
                        </span>
                    )}
                </div>
                {expanded && description && (
                    <p className="mt-2 text-[10px] text-slate-300 leading-relaxed border-t border-white/10 pt-2">
                        {description}
                    </p>
                )}
                {description && (
                    <div className={`text-[9px] mt-1.5 font-bold ${c.text} opacity-50`}>
                        {expanded ? '▲ collapse' : '▼ expand'}
                    </div>
                )}
            </div>
        </div>
    );
};

const GovernanceStructureDisplay: React.FC<GovernanceStructureDisplayProps> = ({ data, country, onNavigate, onBack }) => {
    const gov = data?.governmentApparatus || {};
    const leadership = data?.leadershipOverview || {};
    const wgi = data?.worldGovernanceIndicators || {};
    const freedomHouse = data?.freedomHouseScore;
    const ruleOfLaw = data?.ruleOfLawIndex;
    const keyFigures: any[] = leadership?.keyFigures || [];

    const countryName = country || data?.country || 'Country';
    const govType = leadership?.politicalStructure || data?.governmentStability || 'Democratic Government';
    const headOfState = leadership?.headOfState || 'N/A';

    const tabs = ['Org Chart', 'OSINT Scores', 'Key Figures', 'Party Landscape'] as const;
    const [activeTab, setActiveTab] = useState<typeof tabs[number]>('Org Chart');

    return (
        <div className="min-h-screen bg-slate-950 text-white font-sans">
            {/* ── Header ── */}
            <div className="bg-slate-900 border-b border-slate-800 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
                <div className="flex items-center gap-4">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors text-slate-400 hover:text-white"
                            title="Back to report"
                        >
                            ← Back
                        </button>
                    )}
                    <div>
                        <h1 className="text-lg font-black uppercase tracking-widest text-white">
                            🏛️ Governance Structure Diagram
                        </h1>
                        <p className="text-xs text-slate-400 mt-0.5">
                            {countryName} — Legal, Political &amp; Institutional Framework
                        </p>
                    </div>
                </div>
                <div className="hidden md:flex items-center gap-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Sources:</span>
                    <span className="px-2 py-1 bg-slate-800 rounded-full text-[10px] font-bold text-blue-400">World Bank WGI</span>
                    <span className="px-2 py-1 bg-slate-800 rounded-full text-[10px] font-bold text-indigo-400">Freedom House</span>
                    <span className="px-2 py-1 bg-slate-800 rounded-full text-[10px] font-bold text-purple-400">WJP Rule of Law</span>
                    <span className="px-2 py-1 bg-slate-800 rounded-full text-[10px] font-bold text-emerald-400">IPU Parline</span>
                </div>
            </div>

            {/* ── Gov Type Banner ── */}
            <div className="bg-gradient-to-r from-blue-900/60 via-indigo-900/60 to-purple-900/60 border-b border-slate-800 px-6 py-4 flex flex-col md:flex-row gap-4 items-start md:items-center">
                <div className="flex-1">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Government Type</div>
                    <div className="text-xl font-black text-white mt-1">{govType}</div>
                </div>
                <div className="flex-1">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Head of State / Government</div>
                    <div className="text-lg font-bold text-blue-300 mt-1">{headOfState}</div>
                </div>
                <div className="flex-1">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Stability Outlook</div>
                    <div className="text-sm font-medium text-slate-300 mt-1 line-clamp-2">{leadership?.stabilityOutlook || 'See full report for details.'}</div>
                </div>
            </div>

            {/* ── Tabs ── */}
            <div className="border-b border-slate-800 px-6 bg-slate-900/50">
                <nav className="-mb-px flex gap-6 overflow-x-auto">
                    {tabs.map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`py-3 text-[11px] font-black uppercase tracking-widest whitespace-nowrap border-b-2 transition-colors ${
                                activeTab === tab
                                    ? 'border-blue-500 text-blue-400'
                                    : 'border-transparent text-slate-500 hover:text-slate-300'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </nav>
            </div>

            <div className="p-6">
                {/* ══════════ TAB 1: Org Chart ══════════ */}
                {activeTab === 'Org Chart' && (
                    <div className="space-y-8">
                        <p className="text-[11px] text-slate-500 italic">
                            Click any node to expand its description. Data sourced from AI-synthesised Gemini analysis + World Bank WGI.
                        </p>

                        {/* Tier 0 — State / Nation */}
                        <div className="flex justify-center">
                            <div className="bg-gradient-to-br from-slate-700 to-slate-800 border-2 border-slate-500 rounded-2xl px-8 py-4 shadow-2xl text-center">
                                <div className="text-2xl mb-1">🌍</div>
                                <div className="text-sm font-black uppercase tracking-widest text-white">{countryName}</div>
                                <div className="text-[10px] text-slate-400 mt-0.5">{govType}</div>
                            </div>
                        </div>
                        <Connector />

                        {/* Tier 1 — Three Branches */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Executive */}
                            <div className="flex flex-col items-center gap-3">
                                <OrgNode
                                    title="Executive Branch"
                                    subtitle={headOfState}
                                    description={gov.executive || leadership?.politicalStructure}
                                    branch="executive"
                                    icon="👤"
                                    badge="Head of State"
                                />
                                <Connector />
                                <OrgNode
                                    title="Cabinet / Ministers"
                                    description="Senior executive ministers responsible for policy implementation and departmental oversight."
                                    branch="executive"
                                    icon="🗂️"
                                />
                                <Connector />
                                <OrgNode
                                    title="Civil Service"
                                    description="Permanent administrative apparatus executing government policy."
                                    branch="executive"
                                    icon="🏢"
                                />
                            </div>

                            {/* Legislative */}
                            <div className="flex flex-col items-center gap-3">
                                <OrgNode
                                    title="Legislative Branch"
                                    description={gov.legislative || 'National legislature responsible for law-making and executive oversight.'}
                                    branch="legislative"
                                    icon="📜"
                                    badge="Parliament"
                                />
                                <Connector />
                                <OrgNode
                                    title="Upper Chamber"
                                    description="Senate / upper house providing deliberation and constitutional review."
                                    branch="legislative"
                                    icon="🏛️"
                                />
                                <Connector />
                                <OrgNode
                                    title="Lower Chamber"
                                    description="House of Representatives / lower house — primary legislative body."
                                    branch="legislative"
                                    icon="🗳️"
                                />
                            </div>

                            {/* Judicial */}
                            <div className="flex flex-col items-center gap-3">
                                <OrgNode
                                    title="Judicial Branch"
                                    description={gov.judicial || 'Independent judiciary providing constitutional review, criminal and civil adjudication.'}
                                    branch="judicial"
                                    icon="⚖️"
                                    badge="Supreme Court"
                                />
                                <Connector />
                                <OrgNode
                                    title="High Courts"
                                    description="Appellate courts adjudicating civil, criminal and administrative disputes."
                                    branch="judicial"
                                    icon="🔏"
                                />
                                <Connector />
                                <OrgNode
                                    title="Local Courts"
                                    description="Magistrate and district courts forming first-instance adjudication."
                                    branch="judicial"
                                    icon="🏠"
                                />
                            </div>
                        </div>

                        {/* Tier 2 — Security Apparatus */}
                        <div className="border-t border-slate-800 pt-6">
                            <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 text-center">Security &amp; Intelligence Apparatus</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                                <OrgNode
                                    title="Armed Forces"
                                    description={gov.military || 'National defence forces under civilian executive control.'}
                                    branch="military"
                                    icon="🎖️"
                                    badge="Defence"
                                />
                                <OrgNode
                                    title="Intelligence Services"
                                    description={gov.intelligence || 'Domestic and foreign intelligence agencies operating under executive authority.'}
                                    branch="intelligence"
                                    icon="🔭"
                                    badge="Intel"
                                />
                            </div>
                        </div>

                        {/* Legal Framework Summary */}
                        {data?.legalFramework && (
                            <div className="mt-6 bg-slate-900/60 border border-slate-700 rounded-xl p-5">
                                <h4 className="text-[10px] font-black uppercase tracking-widest text-purple-400 mb-2">Legal Framework</h4>
                                <p className="text-sm text-slate-300">{data.legalFramework}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* ══════════ TAB 2: OSINT Scores ══════════ */}
                {activeTab === 'OSINT Scores' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* World Bank WGI Panel */}
                        <div className="bg-slate-900 border border-slate-700 rounded-xl p-5 space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-[11px] font-black uppercase tracking-widest text-blue-400">World Bank — Worldwide Governance Indicators</h3>
                                <a href="https://info.worldbank.org/governance/wgi/" target="_blank" rel="noopener noreferrer"
                                   className="text-[10px] text-slate-500 hover:text-blue-400 transition-colors">↗ Source</a>
                            </div>
                            <WgiBar label="Rule of Law" value={wgi.ruleOfLaw} icon="⚖️" />
                            <WgiBar label="Government Effectiveness" value={wgi.govEffectiveness} icon="🏛️" />
                            <WgiBar label="Control of Corruption" value={wgi.controlOfCorruption} icon="🔒" />
                            <WgiBar label="Voice & Accountability" value={wgi.voiceAccountability} icon="📢" />
                            <WgiBar label="Political Stability" value={wgi.politicalStability} icon="🕊️" />
                            <WgiBar label="Regulatory Quality" value={wgi.regulatoryQuality} icon="📋" />
                            <p className="text-[9px] text-slate-600 pt-2 border-t border-slate-800">WGI scores range from approx. -2.5 (weakest) to +2.5 (strongest). Percentile shown.</p>
                        </div>

                        {/* Freedom House + WJP Panel */}
                        <div className="space-y-4">
                            <div className="bg-slate-900 border border-slate-700 rounded-xl p-5">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-[11px] font-black uppercase tracking-widest text-indigo-400">Freedom House — Freedom in the World</h3>
                                    <a href="https://freedomhouse.org/report/freedom-world" target="_blank" rel="noopener noreferrer"
                                       className="text-[10px] text-slate-500 hover:text-indigo-400 transition-colors">↗ Source</a>
                                </div>
                                {freedomHouse != null ? (
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-sm text-slate-300">Aggregate Score</span>
                                            <span className={`text-lg font-black ${freedomHouse >= 70 ? 'text-emerald-400' : freedomHouse >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                                                {freedomHouse} / 100
                                            </span>
                                        </div>
                                        <div className="w-full bg-slate-700 h-2 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${freedomHouse >= 70 ? 'bg-emerald-500' : freedomHouse >= 40 ? 'bg-yellow-400' : 'bg-red-500'}`}
                                                style={{ width: `${freedomHouse}%` }}
                                            />
                                        </div>
                                        <p className="text-[10px] text-slate-500">
                                            {freedomHouse >= 70 ? '🟢 Free' : freedomHouse >= 40 ? '🟡 Partly Free' : '🔴 Not Free'}
                                        </p>
                                    </div>
                                ) : (
                                    <p className="text-xs text-slate-500 italic">Score not available for this country in current dataset.</p>
                                )}
                            </div>

                            <div className="bg-slate-900 border border-slate-700 rounded-xl p-5">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-[11px] font-black uppercase tracking-widest text-purple-400">WJP — Rule of Law Index</h3>
                                    <a href="https://worldjusticeproject.org/rule-of-law-index/" target="_blank" rel="noopener noreferrer"
                                       className="text-[10px] text-slate-500 hover:text-purple-400 transition-colors">↗ Source</a>
                                </div>
                                {ruleOfLaw != null ? (
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-sm text-slate-300">Overall Score</span>
                                            <span className={`text-lg font-black ${ruleOfLaw >= 0.7 ? 'text-emerald-400' : ruleOfLaw >= 0.4 ? 'text-yellow-400' : 'text-red-400'}`}>
                                                {ruleOfLaw.toFixed(2)} / 1.00
                                            </span>
                                        </div>
                                        <div className="w-full bg-slate-700 h-2 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${ruleOfLaw >= 0.7 ? 'bg-emerald-500' : ruleOfLaw >= 0.4 ? 'bg-yellow-400' : 'bg-red-500'}`}
                                                style={{ width: `${ruleOfLaw * 100}%` }}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-xs text-slate-500 italic">Index score not available for this country in current dataset.</p>
                                )}
                            </div>

                            {/* Source Attribution */}
                            <div className="bg-slate-900 border border-slate-700 rounded-xl p-4">
                                <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">OSINT Data Sources</h4>
                                <ul className="space-y-1">
                                    {[
                                        { name: 'World Bank WGI', url: 'https://info.worldbank.org/governance/wgi/', colour: 'text-blue-400' },
                                        { name: 'Freedom House', url: 'https://freedomhouse.org/report/freedom-world', colour: 'text-indigo-400' },
                                        { name: 'WJP Rule of Law Index', url: 'https://worldjusticeproject.org/rule-of-law-index/', colour: 'text-purple-400' },
                                        { name: 'IPU Parline (Parliamentary Data)', url: 'https://data.ipu.org/', colour: 'text-emerald-400' },
                                        { name: 'Wikidata — Head of State', url: 'https://query.wikidata.org/', colour: 'text-yellow-400' },
                                    ].map(s => (
                                        <li key={s.name}>
                                            <a href={s.url} target="_blank" rel="noopener noreferrer" className={`text-[10px] font-bold hover:underline ${s.colour}`}>
                                                ↗ {s.name}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {/* ══════════ TAB 3: Key Figures ══════════ */}
                {activeTab === 'Key Figures' && (
                    <div>
                        {keyFigures.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {keyFigures.map((fig: any, i: number) => (
                                    <div key={i} className="bg-slate-900 border border-slate-700 rounded-xl p-4 hover:border-blue-600 transition-colors">
                                        <div className="flex items-start justify-between gap-2 mb-3">
                                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-lg font-black text-white shrink-0">
                                                {(fig.name || '?').charAt(0)}
                                            </div>
                                            <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full ${
                                                fig.influence === 'High' ? 'bg-red-900 text-red-300' :
                                                fig.influence === 'Medium' ? 'bg-yellow-900 text-yellow-300' :
                                                'bg-slate-700 text-slate-400'
                                            }`}>
                                                {fig.influence || 'Unknown'} Influence
                                            </span>
                                        </div>
                                        <div className="font-black text-white text-sm">{fig.name}</div>
                                        <div className="text-[10px] font-bold text-blue-400 uppercase tracking-wider mb-2">{fig.role}</div>
                                        <p className="text-xs text-slate-400 leading-relaxed">{fig.bio}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-16 text-slate-500">
                                <div className="text-4xl mb-3">👤</div>
                                <p className="font-bold">No key figures data available in current report.</p>
                                <p className="text-xs mt-1">Regenerate analysis or check leadership section of full report.</p>
                            </div>
                        )}
                    </div>
                )}

                {/* ══════════ TAB 4: Party Landscape ══════════ */}
                {activeTab === 'Party Landscape' && (
                    <div>
                        {(data?.politicalParties && data.politicalParties.length > 0) ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {data.politicalParties.map((party: any, i: number) => {
                                    const influence = party.influenceLevel?.toLowerCase() || '';
                                    return (
                                        <div key={i} className={`bg-slate-900 rounded-xl border p-4 ${
                                            influence === 'high' ? 'border-blue-600' :
                                            influence === 'medium' ? 'border-yellow-600' :
                                            'border-slate-700'
                                        }`}>
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="font-black text-white text-sm">{party.name}</div>
                                                <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full ${
                                                    influence === 'high' ? 'bg-blue-900 text-blue-300' :
                                                    influence === 'medium' ? 'bg-yellow-900 text-yellow-300' :
                                                    'bg-slate-700 text-slate-400'
                                                }`}>
                                                    {party.influenceLevel || 'Unknown'}
                                                </span>
                                            </div>
                                            <div className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider mb-2">{party.orientation}</div>
                                            <p className="text-xs text-slate-400 leading-relaxed">{party.summary}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-16 text-slate-500">
                                <div className="text-4xl mb-3">🗳️</div>
                                <p className="font-bold">No political party data in current report.</p>
                                <p className="text-xs mt-1">Check the Political Landscape section of the full report.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GovernanceStructureDisplay;
